<template>

    <div>

        <v-layout align-center pa-0>

            <v-flex>

                <v-menu
                    ref="datepickerMenu"
                    lazy
                    :close-on-content-click="false"
                    v-model="shown"
                    transition="scale-transition"
                    offset-y
                    :nudge-bottom="8"
                    min-width="290px"
                    :return-value.sync="date.raw"
                    :style="fullWidth && 'width: 100%'"
                >

                    <v-tooltip dark color="secondary" slot="activator" bottom>
                        <v-btn :data-testid="$options.name + 'v-btn' + 'changeInput'" icon @click="changeInput" :disabled="input.disabled" class="ma-0" slot="activator">                 
                            <svg-icon
                                :color="'primary'"
                                :name="'calendar'"
                                width="18"
                            />
                        </v-btn>
                        <span>{{ $t('select from calendar') }}</span>
                    </v-tooltip>
                    <v-date-picker v-if="readonly" readonly v-model="date.raw" :min="min" :max="max" locale="ru" @input="$refs.datepickerMenu.save(date.raw)"></v-date-picker>
                    <v-date-picker v-else v-model="date.raw" :min="min" :max="max" locale="ru" @input="$refs.datepickerMenu.save(date.raw)"></v-date-picker>

                </v-menu>

                <input type="hidden" :name="name" :value="date.raw"/>

            </v-flex>

            <v-flex pt-3>

                <v-text-field :data-testid="$options.name + 'v-text-field' + 'date.masked'"
                    ref="inputMask"
                    light
                    color="secondary"
                    v-model="date.formatted"
                    :label="$t(label)"
                    return-masked-value
                    mask="##.##.####"
                    outline
                    :placeholder="defaultValue"
                    :hint="hint"
                    :persistent-hint="persistentHint"
                    :prepend-inner-icon="prependInnerIcon"
                    :clearable="clearable && date.masked != ''"
                    @change="(payload) => date.raw = payload"
                    @click:clear="() => date.raw = ''"
                    @keypress="onKeyPress($event)"
                    @input="onInput($event)"
                    @blur="onBlur"
                    :error-messages="state.messages.error"
                ></v-text-field>

            </v-flex>

        </v-layout>

    </div>

</template>

<script lang="ts">
import Vue from 'vue'
import svgIcon from '@/nms/components/media/svg/static/svg-icon.vue'

// Типы
type DateValue = string | Date

interface DateState {
  raw: string
  formatted: string
  masked: string
}

interface InputState {
  mask: boolean
  disabled: boolean
  error: boolean
}

interface StateMessages {
  error: string[]
}

interface StateData {
  messages: StateMessages
}

interface ComponentData {
  shown: boolean
  date: DateState
  input: InputState
  state: StateData
}

export default Vue.extend({
  name: 'INPUT_MENU_DATEPICKER',

  components: {
    svgIcon
  },

  props: {
    value: {
      type: [String, Date] as Vue.PropType<DateValue>,
      default: ''
    },
    name: {
      type: String,
      default: 'date'
    },
    min: {
      type: [String, Date] as Vue.PropType<DateValue | null>,
      default: null
    },
    max: {
      type: [String, Date] as Vue.PropType<DateValue | null>,
      default: null
    },
    data: {
      type: [Array, Object] as Vue.PropType<any[] | Record<string, any> | undefined>,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    btnClass: {
      type: String,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    persistentHint: {
      type: Boolean,
      default: undefined
    },
    prependIcon: {
      type: [String, Boolean] as Vue.PropType<string | boolean>,
      default: 'event'
    },
    prependInnerIcon: {
      type: String,
      default: undefined
    },
    prependInnerIconCb: {
      type: Function as Vue.PropType<(() => void) | null>,
      default: null
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: String,
      default: 'дд.мм.гггг'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    mask: {
      type: Boolean,
      default: false
    },
  },

  data(): ComponentData {
    return {
      shown: false,
      date: {
        raw: '',
        formatted: '',
        masked: '',
      },
      input: {
        mask: false,
        disabled: false,
        error: false,
      },
      state: {
        messages: {
          error: []
        }
      }
    }
  },

  watch: {
    value: {
      handler(newVal: DateValue): void {
        // console.log(this.$options.name, 'watch value newVal', newVal)
        if (typeof newVal === 'string') {
          // console.log(this.$options.name,'watch value typeof string', newVal)
          this.$set(this.date, 'raw', newVal)
        } else if (newVal && newVal instanceof Date && !isNaN(newVal.getTime())) {
          // console.log(this.$options.name,'watch value instanceof Date', newVal)
          const adjustedDate = new Date(newVal.getTime() - (newVal.getTimezoneOffset() * 60000))
          this.date.raw = adjustedDate.toISOString().substring(0, 10)
        }
      }
    },
    'date.raw': function (newVal: string): void {
      // console.log(this.$options.name,'watch date.raw newVal', newVal)
      if (!newVal) {
        // console.log(this.$options.name,'watch date.raw !newVal', newVal)
        this.$emit('dateChange', null)
        // this.reset()
        return
      }
      else {
        // console.log(this.$options.name,'watch date.raw ELSE newVal', newVal)
        this.date.formatted = this.formatDate(newVal)
        let $_data: any = this.unformatDate(newVal)
        // console.log(this.$options.name,'watch date.raw this.date.formatted', this.date.formatted)
        // console.log(this.$options.name,'watch date.raw this.data', this.data)
        if (this.data !== undefined) {
          $_data = {
            data: this.data,
            value: this.unformatDate(newVal)
          }
        }
        // this.$emit('dateChange', $_data)
        this.$emit('change', $_data)
      } 
    },
    'date.masked': function (newVal: string): void {
      // console.log(this.$options.name,'watch date.masked newVal', newVal)
      this.date.raw = this.validate('masked', 'DD.MM.YYYY') ? this.unformatDate(newVal) : ''
    },
    'date.formatted': function (newVal: string): void {
      // console.log(this.$options.name,'watch date.formatted newVal', newVal)
    },
    'input.mask': function (newVal: boolean, oldVal: boolean): void {
      if (newVal) {
        setTimeout(() => {
          const inputElement = this.$refs.inputMask as HTMLInputElement
          inputElement?.focus()
        }, 10)
      } else {
        setTimeout(() => {
          this.shown = true
        }, 10)
      }
    }
  },

  computed: {
    computedIconColor(): string {
      return this.input.mask && this.state.messages.error.length ? 'error' : 'secondary'
    }
  },

  created(): void {
    // console.log(this.$options.name, 'created', 'this.value', this.value)
    if (this.value) {
      // console.log(this.$options.name, 'created', 'typeof this.value', typeof this.value)
      if (typeof this.value === 'string') {
        const value$ = JSON.parse(JSON.stringify(this.value))
        this.date.raw = (value$ as string).split('-').reverse().join('.')
      } else if (this.value instanceof Date) {
        const adjustedDate = new Date(this.value.getTime() - (this.value.getTimezoneOffset() * 60000))
        this.date.raw = adjustedDate.toISOString().substring(0, 10).split('-').reverse().join('.')
      }
      // console.log(this.$options.name, 'created', 'this.date.raw', this.date.raw)
    } else {
      // this.reset()
    }
    this.input.mask = this.mask
  },

  methods: {
    onKeyPress(e: KeyboardEvent): void {
      if (e.keyCode < 48 || e.keyCode > 59) {
        e.preventDefault()
      }
    },

    onKeyUp(e: KeyboardEvent): void {
      // Метод предназначен для переопределения при необходимости
    },

    onInput(e: Event): void {
      this.state.messages.error = []
    },

    validate(type?: string, mask?: string): boolean {
      const $_type = type || 'raw'
      const $_mask = mask || 'YYYY-MM-DD'
      const dateValue = this.date[$_type as keyof DateState]
      const isValid = dateValue && dateValue.length == 10 && (this as any).moment(dateValue, [$_mask]).isValid()
      return !!(dateValue && dateValue.length == 10 && isValid)
    },

    onBlur(): void {
      if (this.input.mask) {
        if (this.validate('masked', 'DD.MM.YYYY')) {
          this.date.raw = this.unformatDate(this.date.masked)
        } else {
          if (this.date.masked && !this.date.raw) {
            this.state.messages.error.push('Неверный формат даты!')
          }
          this.reset()
        }
      }
    },

    changeInput(): void {
      if (this.shown) return
      
      if (this.input.mask) {
        if (this.date.masked && !this.validate('masked', 'DD.MM.YYYY')) {
          this.reset()
        } else {
          this.date.raw = this.unformatDate(this.date.masked)
        }
      } else {
        if (this.validate()) {
          this.date.masked = this.formatDate(this.date.raw)
        }
      }
      this.input.mask = !this.input.mask
      this.state.messages.error = []
    },

    formatDate(value: string): string {
      return value ? value.split('-').reverse().join('.') : ''
    },

    unformatDate(value: string): string {
      return value ? value.split('.').reverse().join('-') : ''
    },

    reset(caller): void {
      // console.log(this.$options.name, 'reset', 'caller', caller)
      setTimeout(() => {
        this.date.raw = ''
        this.date.masked = ''
        this.date.formatted = ''
      }, 10)
    },
  },
})
</script>
